/* =========================================================================
   FF Product JS — purchase form, cart integration, sticky bar.
   Custom elements only; safe to load once per page (defer).
   Sections communicate exclusively through namespaced document events:
     ff:variant-change, ff:selling-plan-change, ff:purchase-state-change,
     ff:cart-add-success, ff:cart-add-error
   ========================================================================= */
(function () {
  'use strict';

  if (customElements.get('ff-product-form')) return;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function dispatch(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail }));
  }

  /* ---------------------------------------------------------------------
     Cart helpers: add line, refresh native drawer, notify the theme.
     Layered so it works in Horizon and degrades gracefully elsewhere.
  --------------------------------------------------------------------- */
  function refreshCartUI(cartJson) {
    return fetch(ROOT + '?sections=cart-drawer-section')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (sections) {
        if (!sections || !sections['cart-drawer-section']) return false;
        var currentInner = document.querySelector('cart-drawer-component');
        var host = currentInner ? currentInner.closest('theme-drawer') || currentInner.closest('dialog') : null;
        if (!host) return false;
        var parsed = new DOMParser().parseFromString(sections['cart-drawer-section'], 'text/html');
        var freshInner = parsed.querySelector('cart-drawer-component');
        var freshHost = freshInner ? freshInner.closest('theme-drawer') || freshInner.closest('dialog') : null;
        if (freshHost && host) {
          var wasOpen = host.hasAttribute('open');
          host.replaceWith(freshHost);
          if (wasOpen && typeof freshHost.open === 'function') freshHost.open();
          return true;
        }
        return false;
      })
      .catch(function () { return false; });
  }

  function openCartDrawer() {
    var inner = document.querySelector('cart-drawer-component');
    var drawer = inner && inner.closest('theme-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return true;
    }
    return false;
  }

  function notifyTheme(cartJson, itemCount) {
    /* Best effort: Shopify standard events (Horizon listens for these). */
    try {
      import('@shopify/events')
        .then(function (mod) {
          if (mod && typeof mod.CartLinesUpdateEvent === 'function') {
            try {
              var ev = new mod.CartLinesUpdateEvent({
                action: 'add',
                promise: Promise.resolve({ cart: cartJson, detail: { itemCount: itemCount } }),
              });
              document.dispatchEvent(ev);
            } catch (e) { /* constructor mismatch: ignore */ }
          }
        })
        .catch(function () { /* no import map for @shopify/events */ });
    } catch (e) { /* dynamic import unsupported */ }
    /* Generic event many themes listen for. */
    dispatch('cart:update', { source: 'ff-product-form', cart: cartJson });
  }

  function addToCart(formData) {
    return fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    }).then(function (response) {
      return response.json().then(function (json) {
        if (!response.ok || json.status) {
          var message = (json && (json.description || json.message)) || 'Unable to add to cart.';
          var err = new Error(message);
          err.shopify = json;
          throw err;
        }
        return json;
      });
    });
  }

  /* ---------------------------------------------------------------------
     <ff-product-form>
  --------------------------------------------------------------------- */
  var FFProductForm = /** @type {any} */ (function () {
    function attach(el) {
      el._abort = new AbortController();
      var signal = el._abort.signal;

      el.form = el.querySelector('form');
      var dataEl = el.querySelector('[data-ff-product-data]');
      try { el.data = JSON.parse(dataEl ? dataEl.textContent : '{}'); } catch (e) { el.data = { variants: [] }; }

      el.sectionId = el.getAttribute('data-section-id');
      el.productId = Number(el.getAttribute('data-product-id'));
      el._syncing = false;
      el._submitting = false;

      var variantInput = el.querySelector('[data-ff-variant-id]');
      el.currentVariant = findVariant(el, variantInput ? Number(variantInput.value) : null) || el.data.variants[0] || null;
      el.currentPlanId = getCheckedPlan(el);

      /* option selects */
      el.querySelectorAll('[data-ff-option-select]').forEach(function (select) {
        select.addEventListener('change', function () { onOptionsChanged(el); }, { signal: signal });
      });

      /* plan radios */
      el.querySelectorAll('[data-ff-plan-radio]').forEach(function (radio) {
        radio.addEventListener('change', function () { onPlanChanged(el); }, { signal: signal });
      });

      /* quantity */
      var qty = el.querySelector('[data-ff-qty]');
      if (qty) {
        var input = qty.querySelector('input[name="quantity"]');
        var step = function (dir) {
          var stepBy = Number(input.step || 1) * dir;
          var next = (Number(input.value) || 1) + stepBy;
          var min = Number(input.min || 1);
          var max = input.max ? Number(input.max) : Infinity;
          input.value = String(Math.max(min, Math.min(max, next)));
        };
        qty.querySelector('[data-ff-qty-minus]').addEventListener('click', function () { step(-1); }, { signal: signal });
        qty.querySelector('[data-ff-qty-plus]').addEventListener('click', function () { step(1); }, { signal: signal });
      }

      /* submit */
      if (el.form) {
        el.form.addEventListener('submit', function (event) { onSubmit(el, event); }, { signal: signal });
      }

      /* cross-section sync */
      el._onExternalState = function (event) {
        var d = event.detail || {};
        if (d.productId !== el.productId) return;
        if (d.sourceSectionId === el.sectionId) return;
        applyExternalState(el, d);
      };
      document.addEventListener('ff:purchase-state-change', el._onExternalState, { signal: signal });

      updateUI(el);
    }

    function findVariant(el, id) {
      return el.data.variants.find(function (v) { return v.id === id; }) || null;
    }

    function getCheckedPlan(el) {
      var checked = el.querySelector('[data-ff-plan-radio]:checked');
      return checked && checked.value ? checked.value : null;
    }

    function onOptionsChanged(el) {
      var selects = Array.prototype.slice.call(el.querySelectorAll('[data-ff-option-select]'));
      var chosen = selects
        .sort(function (a, b) { return Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex); })
        .map(function (s) { return s.value; });
      var match = el.data.variants.find(function (v) {
        return v.options.every(function (value, i) { return value === chosen[i]; });
      });
      el.currentVariant = match || null;
      updateUI(el);
      emitState(el, 'ff:variant-change');
    }

    function onPlanChanged(el) {
      el.currentPlanId = getCheckedPlan(el);
      updateUI(el);
      emitState(el, 'ff:selling-plan-change');
    }

    function currentAllocation(el) {
      if (!el.currentVariant || !el.currentPlanId) return null;
      return el.currentVariant.allocations.find(function (a) {
        return String(a.planId) === String(el.currentPlanId);
      }) || null;
    }

    function updateUI(el) {
      var v = el.currentVariant;
      var allocation = currentAllocation(el);

      /* hidden inputs — one-time purchases never submit selling_plan */
      var variantInput = el.querySelector('[data-ff-variant-id]');
      var planInput = el.querySelector('[data-ff-selling-plan]');
      if (variantInput && v) variantInput.value = String(v.id);
      if (planInput) {
        if (allocation) {
          planInput.value = String(el.currentPlanId);
          planInput.disabled = false;
        } else {
          planInput.value = '';
          planInput.disabled = true;
        }
      }

      /* plan availability per variant */
      el.querySelectorAll('[data-ff-plan-radio]').forEach(function (radio) {
        if (!radio.value) return;
        var ok = v && v.allocations.some(function (a) { return String(a.planId) === String(radio.value); });
        radio.disabled = !ok;
        var row = radio.closest('.ff-option-row');
        if (row) row.classList.toggle('ff-option-row--disabled', !ok);
        if (!ok && radio.checked) {
          var fallback = el.querySelector('[data-ff-plan-radio][value=""]') ||
            el.querySelector('[data-ff-plan-radio]:not(:disabled)');
          if (fallback) { fallback.checked = true; el.currentPlanId = fallback.value || null; }
        }
      });

      /* row prices for the active variant */
      el.querySelectorAll('[data-ff-row-price]').forEach(function (node) {
        var key = node.getAttribute('data-ff-row-price');
        if (!v) return;
        if (key === 'one-time') {
          node.textContent = v.priceFormatted;
        } else {
          var a = v.allocations.find(function (x) { return String(x.planId) === String(key); });
          if (a) node.textContent = a.priceFormatted;
        }
      });

      /* main price line */
      var priceEl = el.querySelector('[data-ff-price]');
      var compareEl = el.querySelector('[data-ff-compare]');
      var recurringEl = el.querySelector('[data-ff-recurring]');
      var active = allocation || v;
      if (priceEl && active) priceEl.textContent = active.priceFormatted;
      if (compareEl && active) {
        var hasCompare = active.compareAtPrice && active.compareAtPrice > (active.price || 0);
        if (allocation) hasCompare = allocation.compareAtPrice > 0;
        compareEl.hidden = !hasCompare;
        if (hasCompare) compareEl.textContent = active.compareFormatted;
      }
      if (recurringEl) {
        if (allocation) {
          recurringEl.hidden = false;
          recurringEl.textContent = allocation.groupName + ' · ' + allocation.planName;
        } else {
          recurringEl.hidden = true;
        }
      }

      /* plan disclosure visibility */
      var disclosure = el.querySelector('[data-ff-plan-disclosure]');
      if (disclosure) disclosure.hidden = !allocation;

      /* button state */
      var button = el.querySelector('[data-ff-add-button]');
      var label = el.querySelector('[data-ff-add-label]');
      var buttonPrice = el.querySelector('[data-ff-button-price]');
      if (button) {
        var available = Boolean(v && v.available);
        button.disabled = !available || el._submitting;
        if (label) {
          if (!v) label.textContent = label.dataset.unavailable || 'Unavailable';
          else if (!available) label.textContent = label.dataset.soldOut || 'Sold out';
          else if (label.dataset.original) label.textContent = label.dataset.original;
          else label.dataset.original = label.textContent;
        }
        if (buttonPrice && active) buttonPrice.textContent = active.priceFormatted;
      }
    }

    function emitState(el, primaryEvent) {
      if (el._syncing) return;
      var v = el.currentVariant;
      var allocation = currentAllocation(el);
      var detail = {
        productId: el.productId,
        variantId: v ? v.id : null,
        sellingPlanId: allocation ? allocation.planId : null,
        priceFormatted: allocation ? allocation.priceFormatted : (v ? v.priceFormatted : null),
        priceRaw: v ? v.price : null,
        available: Boolean(v && v.available),
        image: v ? v.image : null,
        mediaPosition: v ? v.mediaPosition : null,
        variantTitle: v ? v.title : null,
        sourceSectionId: el.sectionId,
      };
      dispatch(primaryEvent, detail);
      dispatch('ff:purchase-state-change', detail);
    }

    function applyExternalState(el, d) {
      el._syncing = true;
      try {
        var v = findVariant(el, d.variantId);
        if (v) {
          el.currentVariant = v;
          el.querySelectorAll('[data-ff-option-select]').forEach(function (select) {
            var idx = Number(select.dataset.optionIndex);
            if (v.options[idx] != null) select.value = v.options[idx];
          });
        }
        var planValue = d.sellingPlanId ? String(d.sellingPlanId) : '';
        var radio = el.querySelector('[data-ff-plan-radio][value="' + planValue + '"]');
        if (radio && !radio.disabled) {
          radio.checked = true;
          el.currentPlanId = planValue || null;
        }
        updateUI(el);
      } finally {
        el._syncing = false;
      }
    }

    function onSubmit(el, event) {
      event.preventDefault();
      if (el._submitting || !el.form) return;
      var button = el.querySelector('[data-ff-add-button]');
      var errorEl = el.querySelector('[data-ff-error]');
      if (errorEl) errorEl.textContent = '';

      el._submitting = true;
      if (button) { button.classList.add('ff-is-loading'); button.disabled = true; }

      var formData = new FormData(el.form);

      addToCart(formData)
        .then(function (line) {
          return fetch(ROOT + 'cart.js')
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (cart) {
              var count = cart ? cart.item_count : null;
              notifyTheme(cart, count);
              return refreshCartUI(cart).then(function (refreshed) {
                if (!refreshed) openCartDrawer();
                dispatch('ff:cart-add-success', {
                  productId: el.productId,
                  variantId: line.variant_id || null,
                  sellingPlanId: el.currentPlanId,
                  sourceSectionId: el.sectionId,
                  cartItemCount: count,
                });
              });
            });
        })
        .catch(function (error) {
          if (errorEl) errorEl.textContent = error.message || 'Something went wrong. Please try again.';
          dispatch('ff:cart-add-error', {
            productId: el.productId,
            message: error.message,
            sourceSectionId: el.sectionId,
          });
        })
        .then(function () {
          el._submitting = false;
          if (button) { button.classList.remove('ff-is-loading'); }
          updateUI(el);
        });
    }

    var proto = Object.create(HTMLElement.prototype);
    proto.connectedCallback = function () { attach(this); };
    proto.disconnectedCallback = function () { if (this._abort) this._abort.abort(); };

    function Ctor() { return Reflect.construct(HTMLElement, [], Ctor); }
    Ctor.prototype = proto;
    return Ctor;
  })();

  customElements.define('ff-product-form', FFProductForm);

  /* ---------------------------------------------------------------------
     <ff-sticky-bar>
  --------------------------------------------------------------------- */
  var FFStickyBar = (function () {
    function attach(el) {
      el._abort = new AbortController();
      var signal = el._abort.signal;

      el.productId = Number(el.getAttribute('data-product-id'));
      el.sectionId = el.getAttribute('data-section-id');
      var bar = el.querySelector('.ff-sticky');
      var trigger = el.getAttribute('data-trigger') || 'after-first-screen';

      /* Visibility */
      var setVisible = function (visible) {
        if (bar) bar.classList.toggle('ff-sticky--visible', visible);
      };
      var evaluate = function () {
        var doc = document.documentElement;
        var max = doc.scrollHeight - doc.clientHeight;
        var y = window.scrollY || 0;
        var threshold = trigger === 'immediately' ? 10 : window.innerHeight * 0.85;
        setVisible(y > threshold && y < max - 200);
      };
      window.addEventListener('scroll', evaluate, { passive: true, signal: signal });
      window.addEventListener('resize', evaluate, { passive: true, signal: signal });
      evaluate();

      /* Sync with purchase state from other sections */
      document.addEventListener('ff:purchase-state-change', function (event) {
        var d = event.detail || {};
        if (d.productId !== el.productId || d.sourceSectionId === el.sectionId) return;
        var priceEl = el.querySelector('[data-ff-sticky-price]');
        var titleVariant = el.querySelector('[data-ff-sticky-variant]');
        var thumb = el.querySelector('[data-ff-sticky-thumb]');
        var variantInput = el.querySelector('[data-ff-variant-id]');
        var planInput = el.querySelector('[data-ff-selling-plan]');
        var button = el.querySelector('[data-ff-sticky-add]');
        if (priceEl && d.priceFormatted) priceEl.textContent = d.priceFormatted;
        if (titleVariant && d.variantTitle) titleVariant.textContent = d.variantTitle === 'Default Title' ? '' : ' — ' + d.variantTitle;
        if (thumb && d.image) {
          var img = thumb.querySelector('img');
          if (img) img.src = d.image;
          else {
            img = document.createElement('img');
            img.alt = '';
            img.width = 44;
            img.height = 44;
            img.src = d.image;
            thumb.appendChild(img);
          }
        }
        if (variantInput && d.variantId) variantInput.value = String(d.variantId);
        if (planInput) {
          if (d.sellingPlanId) { planInput.value = String(d.sellingPlanId); planInput.disabled = false; }
          else { planInput.value = ''; planInput.disabled = true; }
        }
        if (button) button.disabled = !d.available;
      }, { signal: signal });

      /* Add to cart via its own mini form */
      var form = el.querySelector('form');
      if (form) {
        form.addEventListener('submit', function (event) {
          event.preventDefault();
          var button = el.querySelector('[data-ff-sticky-add]');
          if (el._submitting) return;
          el._submitting = true;
          if (button) { button.classList.add('ff-is-loading'); button.disabled = true; }
          addToCart(new FormData(form))
            .then(function () {
              return fetch(ROOT + 'cart.js')
                .then(function (r) { return r.ok ? r.json() : null; })
                .then(function (cart) {
                  notifyTheme(cart, cart ? cart.item_count : null);
                  return refreshCartUI(cart).then(function (refreshed) {
                    if (!refreshed) openCartDrawer();
                    dispatch('ff:cart-add-success', { productId: el.productId, sourceSectionId: el.sectionId });
                  });
                });
            })
            .catch(function (error) {
              dispatch('ff:cart-add-error', { productId: el.productId, message: error.message, sourceSectionId: el.sectionId });
              var live = el.querySelector('[data-ff-error]');
              if (live) live.textContent = error.message || 'Something went wrong.';
            })
            .then(function () {
              el._submitting = false;
              if (button) { button.classList.remove('ff-is-loading'); button.disabled = false; }
            });
        }, { signal: signal });
      }
    }

    var proto = Object.create(HTMLElement.prototype);
    proto.connectedCallback = function () { attach(this); };
    proto.disconnectedCallback = function () { if (this._abort) this._abort.abort(); };
    function Ctor() { return Reflect.construct(HTMLElement, [], Ctor); }
    Ctor.prototype = proto;
    return Ctor;
  })();

  if (!customElements.get('ff-sticky-bar')) customElements.define('ff-sticky-bar', FFStickyBar);
})();
